import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

interface SmsInviteModalProps {
  visible?: boolean;
  onClose?: () => void;
  referralCode?: string;
  userName?: string;
}

const API_URL = (import.meta?.env?.VITE_API_URL as string) || 'https://api.atootbandan.com/api';

export function SmsInviteModal({ visible = false, onClose, referralCode = 'ATOOTBANDAN', userName = 'Your Friend' }: SmsInviteModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ referralCode, userName });

  // Listen for WebView messages to show the modal
  useEffect(() => {
    // Don't show web modal if running in React Native WebView - let native modal handle it
    if ((window as any).isNativeApp || (window as any).ReactNativeWebView) {
      logger.log('📲 [SMS Invite Web] Running in React Native WebView - native modal will be used');
      return;
    }
    
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        
        if (data.type === 'SHOW_SMS_INVITE') {
          logger.log('📲 [SMS Invite] Received message from WebView:', data);
          setModalData({
            referralCode: data.referralCode || 'ATOOTBANDAN',
            userName: data.userName || 'Your Friend'
          });
          setShowModal(true);
        }
      } catch (error) {
        logger.error('❌ [SMS Invite] Error parsing message:', error);
      }
    };
    
    // Also listen for custom events dispatched by the early script
    const handleWebViewMessage = (event: any) => {
      const data = event.detail;
      
      if (data && data.type === 'SHOW_SMS_INVITE') {
        logger.log('📲 [SMS Invite] Received custom event:', data);
        setModalData({
          referralCode: data.referralCode || 'ATOOTBANDAN',
          userName: data.userName || 'Your Friend'
        });
        setShowModal(true);
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('webview-message', handleWebViewMessage);
    
    // No need to redefine showSmsInviteModal as it's now in index.html
    // But we can update it to use React state
    const originalShowSms = (window as any).showSmsInviteModal;
    (window as any).showSmsInviteModal = () => {
      logger.log('📲 [SMS Invite] Manual trigger via window function');
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setModalData({
            referralCode: user.referralCode || 'ATOOTBANDAN',
            userName: user.fullName || 'Your Friend'
          });
          setShowModal(true);
        } catch (e) {
          logger.error('❌ [SMS Invite] Error parsing user data:', e);
        }
      } else {
        // Fallback to original if no user data
        if (originalShowSms) originalShowSms();
      }
    };

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('webview-message', handleWebViewMessage);
    };
  }, []);

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  const handleSkip = async () => {
    try {
      setIsSubmitting(true);
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        logger.error('❌ [SMS Invite] No auth token found');
        handleClose();
        return;
      }

      // Mark SMS as sent (user skipped)
      await axios.put(
        `${API_URL}/auth/update-sms-status`,
        { smsSent: true },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update user in localStorage
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        user.smsSent = true;
        localStorage.setItem('user', JSON.stringify(user));
      }

      logger.log('✅ [SMS Invite] Marked as sent (skipped)');
      toast.success('You can invite friends anytime from your profile!');
      handleClose();
    } catch (error) {
      logger.error('❌ [SMS Invite] Error updating status:', error);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendInvite = () => {
    // This is a placeholder - in the web version, we can't directly access contacts or SMS
    // So we'll just provide the referral code for the user to share
    const message = `Hi! I'm using Atootbandan matrimonial app and thought you might be interested. Use my referral code ${modalData.referralCode} to get started! Download here: https://atootbandan.com`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(message).then(() => {
      toast.success('Referral message copied to clipboard!');
      handleSkip(); // Mark as sent
    }).catch(() => {
      toast.error('Failed to copy message');
    });
  };

  if (!visible && !showModal) return null;

  return (
    <Dialog open={visible || showModal} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">
            Invite Friends & Earn Rewards
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="text-center">
            <div className="text-4xl mb-2">🎁</div>
            <p className="text-gray-600">
              Share Atootbandan with your friends and earn rewards when they join!
            </p>
          </div>

          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <p className="text-sm text-gray-700 mb-2">Your Referral Code:</p>
            <p className="text-2xl font-bold text-pink-600 text-center">
              {modalData.referralCode}
            </p>
          </div>

          <div className="space-y-2">
            <Button
              onClick={handleSendInvite}
              disabled={isSubmitting}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white"
            >
              Copy Referral Message
            </Button>
            
            <Button
              onClick={handleSkip}
              disabled={isSubmitting}
              variant="outline"
              className="w-full"
            >
              {isSubmitting ? 'Please wait...' : 'Maybe Later'}
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            You can access your referral code anytime from your profile
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}